<template>
  <div>
    <b-sidebar id="template-sidebar" @shown="onShown()" title="Apply template" v-model="sidebarOpen" right shadow no-header>
      <div class="px-3 py-2 mt-2">
        <span class="Masthead">Apply template</span>
      </div>

      <div class="px-3 py-2 template-form">
        <rails-b-form ref="templateForm">
          <input name="template[slot]" :value="form.templateSlot" type="hidden">
          <input name="template[collections]" :value="form.templateCollections" type="hidden">
          <input name="template[expire_days_enabled]" :value="form.expireDaysEnabled" type="hidden">
          <input name="template[expire_days]" :value="form.expireDays" type="hidden">
          <input name="template[security_setting]" :value="form.securitySetting" type="hidden">

          <b-form-group id="template-name-group" label="Template name" label-for="template-name">
            <b-form-input id="template-name" name="template[name]" v-model="form.templateName" type="text" placeholder="Template name" :readonly="!isCustom" required/>
          </b-form-group>

          <b-form-group v-if="isCustom" id="template-description-group" label="Template description" label-for="template-description">
            <b-textarea id="template-description" name="template[description]" v-model="form.templateDescription"/>
          </b-form-group>

          <b-form-group id="default-title-group" label="Default title" label-for="template-title">
            <b-form-input id="template-title" name="template[item_title]" v-model="form.defaultTitle" type="text" placeholder="Default title" required/>
          </b-form-group>

          <b-form-group id="default-title-group" label="Default description" label-for="template-item-description">
            <b-textarea id="template-item-description" name="template[item_description]" v-model="form.defaultDescription" type="text" placeholder="Default description"/>
          </b-form-group>

          <div class="d-flex align-items-center justify-content-between mt-2">
            <label for="comment-enabled" class="switch-label">Anyone can comment</label>
            <b-form-checkbox id="comment-enabled" name="template[comment_enabled]" v-model="form.commentEnabled" switch size="lg"/>
          </div>

          <div class="d-flex align-items-center justify-content-between mt-2">
            <label for="button-enabled" class="switch-label">Add button</label>
            <b-form-checkbox id="button-enabled" name="template[button_enabled]" v-model="form.buttonEnabled" switch size="lg"/>
          </div>

          <div class="spacer"></div>

          <b-form-group id="button-text-group" label="Button text" label-for="cta-text" v-show="form.buttonEnabled">
            <b-form-input id="cta-text" name="cta[text]" v-model="form.buttonText" type="text" placeholder="Button text" required>
            </b-form-input>
          </b-form-group>

          <b-form-group id="button-url-group" label="Button URL" label-for="button-url" v-show="form.buttonEnabled">
            <b-form-input id="button-url" name="cta[link]" v-model="form.buttonLink" type="text" placeholder="Button link" required>
            </b-form-input>
          </b-form-group>

          <security-modal @selectSecuritySettings="updateSecuritySettings" :settings="formSecuritySettings"></security-modal>
          <div class="button-bg" v-b-modal.security-modal data-testid="template-security-settings-button">
            <span class="label">{{ securitySettingsLabel }}</span>
          </div>

          <collections-modal @selectCollections="updateCollections" :selected="form.templateCollections"></collections-modal>

          Automatically add to collections
          <div class="button-bg" v-b-modal.collections-modal data-testid="template-add-collections-button">
            <span class="label">{{ addToCollectionsLabel }}</span>
          </div>

          <p>
            <a :href="brandingUrl" class="customize-branding" target="_blank">
              Customize branding <img src="@img/icons-new.svg" alt="(new)">
            </a>
          </p>
        </rails-b-form>

        <div class="footer">
          <b-button @click="submitForm()" class="mt-2" variant="primary" block>Save &amp; apply template</b-button>
          <b-button variant="outline-secondary" block @click="resetAndToggleSidebar">Cancel</b-button>
        </div>
      </div>
    </b-sidebar>
  </div>
</template>

<script>
import _ from "lodash";
import track from "@/src/tracking.js";
import RailsBForm from "@/src/components/rails_b_form.vue";
import SecurityModal from "@/src/components/viewer/security_modal.vue";
import CollectionsModal from "@/src/components/viewer/collections_modal.vue";

export default {
  components: { RailsBForm, CollectionsModal, SecurityModal },
  props: [ "defaults" ],
  data: function() {
    return {
      sidebarOpen: false,
      form: {
        templateId: null,
        templateSlot: null,
        templateName: null,
        templateDescription: null,
        templateCollections: [],
        defaultTitle: null,
        commentEnabled: true,
        buttonEnabled: true,
        buttonText: null,
        buttonLink: null,
        expireDaysEnabled: false,
        expireDays: 7,
        securitySetting: "un"
      }
    }
  },
  watch: {
    form: {
      deep: true,
      handler: function(value) {
        if (this.startForm === null && this.apiLoaded) {
          this.startForm = _.clone(value);
        }
      }
    },
    defaultTitleSettings: function(value) {
      this.$emit("update:title", value);
    },
    defaultDescriptionSettings: function(value) {
      this.$emit("update:description", value);
    },
    buttonSettings: function(val) {
      this.$emit("update:button-settings", val);
    },
    sidebarOpen: function(val) {
      this.$emit("update:sidebar-open", val);
      if (val) {
        this.$root.$refs.viewerSidebarPanels.hide();
      } else {
        this.$root.$refs.viewerSidebarPanels.show();
      }
    }
  },
  computed: {
    isCustom: function() {
      return !["sales", "bug", "support", "announcement"].includes(this.form.templateSlot);
    },
    defaultTitleSettings: function() {
      return this.form.defaultTitle;
    },
    defaultDescriptionSettings: function(){
      return this.form.defaultDescription;
    },
    buttonSettings: function() {
      const { buttonEnabled, buttonText, buttonLink } = this.form;
      return { buttonEnabled, buttonText, buttonLink };
    },
    securitySettingsLabel: function() {
      return "Custom security settings";
    },
    formSecuritySettings() {
      return {
        expireDaysEnabled: this.form.expireDaysEnabled,
        expireDays: this.form.expireDays,
        securitySetting: this.form.securitySetting
      }
    },
    addToCollectionsLabel: function() {
      const selected = this.form.templateCollections.length;
      if (selected == 0) {
        return "Select collections";
      } else if (selected == 1) {
        return "1 collection selected";
      } else {
        return `${selected} collections selected`;
      }
    },
    brandingUrl: function() {
      if (this.$store.state.currentOrganization.id) {
        return this.$root.routes.customize_settings_organization_path(this.$store.state.currentOrganization.id);
      } else {
        return "#";
      }
    }
  },
  methods: {
    updateSecuritySettings(settings) {
      this.$set(this.form, "expireDaysEnabled", settings.expireDaysEnabled);
      this.$set(this.form, "expireDays", settings.expireDays);
      this.$set(this.form, "securitySetting", settings.securitySetting);
    },
    updateCollections(collections) {
      this.$set(this.form, "templateCollections", collections);
    },
    onShown() {
      const vm = this;
      this.loadTemplate().then(function() {
        track("viewer_template_editor_opened", {
          user_id: vm.$store.state.user.id,
          item_id: vm.$store.state.item.id,
          org_id: vm.$store.state.currentOrganization.id,
          ...vm.form
        })
      });
    },
    loadTemplate() {
      const vm = this;
      let search = null;

      if (vm.form.templateId) {
        search = { template_id: vm.form.templateId };
      } else {
        search = { slot: vm.form.templateSlot };
      }

      return vm.$store.dispatch("searchTemplates", search).
        then(function(resp) {
          vm.setResponse(resp);
          vm.apiLoaded = true;
        }).
        catch(function(error) {
          if (error.response?.status == 404) { return; }
          vm.$root.toastError(`Failed to get template: ${error}`);
        });
    },
    setResponse(resp) {
      const vm = this;
      const template = resp.data?.data?.template?.attributes;
      const template_id = resp.data?.data?.template?.id;
      const cta = resp.data?.included?.[0]?.attributes;

      if (template) {
        vm.$set(vm.form, "templateId", template_id);
        vm.$set(vm.form, "templateSlot", template.slot);
        vm.$set(vm.form, "templateName", template.name);
        vm.$set(vm.form, "templateDescription", template.description);
        vm.$set(vm.form, "templateCollections", template.collections);
        vm.$set(vm.form, "defaultTitle", template.item_title);
        vm.$set(vm.form, "defaultDescription", template.item_description);
        vm.$set(vm.form, "commentEnabled", template.comment_enabled);
        vm.$set(vm.form, "buttonEnabled", template.button_enabled);
        vm.$set(vm.form, "expireDaysEnabled", template.expire_days_enabled);
        vm.$set(vm.form, "expireDays", template.expire_days);
        vm.$set(vm.form, "securitySetting", template.security_setting);

        if (cta) {
          vm.$set(vm.form, "buttonText", cta.text);
          vm.$set(vm.form, "buttonLink", cta.link);
        }
      }
    },
    loadSidebar(value) {
      if (value.templateSlot != this.form.templateSlot) {
        this.$set(this.form, "templateId", null);
      }
      this.form = { ...this.form, ...value };
    },
    toggleSidebar: function() {
      this.sidebarOpen = !this.sidebarOpen;
    },
    resetAndToggleSidebar: function() {
      this.$root.clearPreview();
      this.toggleSidebar();
    },
    applyCurrentTemplate: function() {
      const vm = this;
      if (!vm.form.templateId) {
        vm.$root.toastError("Could not apply unsaved template!");
        return;
      }

      this.$root.commitPreview();
      vm.$store.dispatch("applyTemplate", { templateId: vm.form.templateId, itemId: vm.$store.state.item.id }).
        then(function(data) {
          data.data.included.forEach(function(included) {
            if (included.type === "call_to_action") {
              vm.$store.commit("setCallToAction", included);
            }
          });
        }).
        catch(function(error) {
          vm.$root.toastError(`Could not apply template: ${error.response.data.errors[0].detail}`)
        });
    },
    submitForm: function() {
      const vm = this;

      vm.$refs.templateForm.submit(vm.$root.routes.api_v5_template_index_path()).
        then(function(resp) {
          vm.saveOnClose = true;
          vm.setResponse(resp);
          vm.applyCurrentTemplate();
          vm.toggleSidebar();
          vm.$root.toastSuccess("Template applied");
        }).
        catch(function(error) {
          vm.$root.toastError(error.response.data.errors[0].detail)
        });
    }
  }
}
</script>

<style lang="scss" scoped>
@import '@/packs/application.scss';

.footer {
  margin-top: 8rem;
}

.customize-branding {
  margin-top: 2rem;
  margin-bottom: 2rem;
  text-align: center;
  font-family: SFPro;
  font-size: 0.8rem;
  font-weight: normal;
  font-stretch: normal;
  font-style: normal;
  line-height: normal;
  letter-spacing: normal;
  color: $bright-blue;

  :visited {
    color: $bright-blue;
  }
  :hover {
    color: $bright-blue;
  }
}

// Set general font style for labels, because the labels are dynamically inserted by
// bootstrap-vue, and messing with them affects the global scope.
.template-form {
  font-family: SFPro;
  font-size: 0.75rem;
  font-weight: normal;
  font-stretch: normal;
  font-style: normal;
  line-height: normal;
  letter-spacing: normal;
  color: $c-dark-gray;

  label.switch-label {
    margin: 0;
    font-size: 0.75rem;
    font-weight: normal;
    font-stretch: normal;
    font-style: normal;
    line-height: normal;
    letter-spacing: normal;
  }
}

.Masthead {
  font-family: SFPro;
  font-size: 1.125rem;
  font-weight: 600;
  font-stretch: normal;
  font-style: normal;
  line-height: normal;
  letter-spacing: normal;
  color: #000000;
}

.button-bg {
  width: 90%;
  height: 2rem;
  border-radius: 4px;
  border: solid 1px #d4d4d4;
  background-color: $white;
  margin-left: auto;
  margin-right: auto;
  margin-top: 1rem;
  margin-bottom: 1rem;
  cursor: pointer;
}

.label {
  white-space: nowrap;
  height: 0.875rem;
  margin: 0.5rem 0;
  display: block;
  font-family: SFPro;
  font-size: 0.75rem;
  font-weight: 600;
  font-stretch: normal;
  font-style: normal;
  line-height: normal;
  letter-spacing: normal;
  text-align: center;
  color: $bright-blue;
}

div.spacer {
  display: block;
  height: 1.5rem;
  width: 0;
  clear: both;
}
</style>
